import React, {FC, useState} from "react";
import useTranslate from "../../core/languages/hooks/useTranslate";
import NcImage from "../../ui/shared/NcImage/NcImage";
import {StarIcon} from "@heroicons/react/24/solid";
import BookingItem from "../../booking/types/BookingItem";
import BookingItemGuest from "../../booking/types/BookingItemGuest";
import moment from "moment";
import Hotel from "../../hotels/types/Hotel";
import useCustomerContext from "../../customers/hooks/useCustomerContext";
import BookingPriceDetailsByNights from "./BookingPriceDetailsByNights";
import useGetBookingHotelRoomRatesInfo from "../hooks/useGetBookingHotelRoomRatesInfo";
import {formatNumber} from "../../common/utils/number";

export interface BookingItemCardProps {
    item: BookingItem;
    hotel: Hotel
    currencySymbol: string
}

const BookingItemCard: FC<BookingItemCardProps> = ({
    item,
    hotel,
    currencySymbol
}) => {
    const t = useTranslate();
    const {languageCode} = useCustomerContext();
    const [isOpenModalPriceDetail, setIsOpenModalPriceDetail] = useState(false);
    const bookItemPriceDetailQuery = useGetBookingHotelRoomRatesInfo(
      item.internalId, item.departureDate, {execute: isOpenModalPriceDetail && !!item.internalId}
    );

    const {data: bookPriceDetail, isLoading: isLoadingBookPriceDetail} = bookItemPriceDetailQuery;

    const closeModal = () => setIsOpenModalPriceDetail(false);
    return (
        <>
            <div className="flex flex-col sm:flex-row sm:items-center">
                <div className="flex-shrink-0 w-full sm:w-40">
                    <div className=" aspect-w-4 aspect-h-3 sm:aspect-h-4 rounded-2xl overflow-hidden">
                        <NcImage src={hotel.imgContentUrl} />
                    </div>
                </div>
                <div className="py-5 sm:px-5 space-y-3 max-w-[33%]">
                    <div>
                        <span className="text-base font-medium mt-1 block">
                            <div className="flex items-end">
                                {[...Array(hotel.stars)].map((value, index) => <StarIcon className="w-[18px] h-[18px] text-orange-500" key={index} />)}
                            </div>
                            {hotel.name}
                        </span>
                    </div>
                    <span className="block  text-sm text-neutral-500 dark:text-neutral-400">
                            {t("checkout.from")}: {moment(item.arrivalDate).format('DD/MM/YYYY')}
                    </span>
                    <span className="block  text-sm text-neutral-500 dark:text-neutral-400">
                            {t("checkout.to")}: {moment(item.departureDate).format('DD/MM/YYYY')}
                    </span>
                    <div className="w-10 border-b border-neutral-200  dark:border-neutral-700"></div>
                </div>
                <div className="py-5 sm:px-5 space-y-3 w-4/6">
                    <div className="flex flex-col space-y-4">
                        <div className="flex justify-between text-neutral-6000 dark:text-neutral-300 font-semibold">
                            <span>{item.roomHotelEvent.trType[languageCode]}</span>
                        </div>
                        <div className="flex justify-between text-neutral-6000 dark:text-neutral-300">
                            <span>{t("bookOk.occupancy")}</span>
                            <span className="ml-2">
                            {[...Array(item.roomHotelEvent.adults)].map((value, index) =>
                                <i className="las la-user la-2x" key={index + item.id}></i>
                            )}
                                {[...Array(item.roomHotelEvent.children)].map((value, index) =>
                                    <i className="text-2xl las la-user" key={index + item.id}></i>
                                )}
                        </span>
                        </div>
                        {item.hotelCheckInTime && (
                          <div className="flex justify-between text-neutral-6000 dark:text-neutral-300">
                              <span>{t("bookOk.arrivalHour")}</span>
                              <span>{item.hotelCheckInTime}</span>
                          </div>
                        )}
                        <div className="border-b border-neutral-200 dark:border-neutral-700"></div>
                            {item.bookingHotelRoomGuests && item.bookingHotelRoomGuests.map((guest:BookingItemGuest, index) => (
                                <div className="flex justify-between font-semibold" key={index}>
                                    <div className="flex flex-row">
                                        <span>{t("bookOk.guest")} {guest.number} {index >= item.roomHotelEvent.occupancy - item.roomHotelEvent.children ? ` (${t("booking.child")})` : null}</span>
                                    </div>
                                    <div>{guest.name} {guest.lastName}</div>
                                </div>
                            ))}
                        <div
                          onClick={() => setIsOpenModalPriceDetail(true)}
                          className="flex justify-between font-semibold cursor-pointer"
                        >
                            <span>{t("bookOk.totalAmount")}</span>
                            <div>{formatNumber(item.priceWithTaxes)} <span dangerouslySetInnerHTML={{ __html:currencySymbol}} /></div>
                        </div>
                    </div>
                </div>
            </div>
            <BookingPriceDetailsByNights
              bookPriceDetail={bookPriceDetail}
              totalPrice={item.priceWithTaxes}
              currencySymbol={currencySymbol}
              isLoadingBookPriceDetail={isLoadingBookPriceDetail}
              openModal={isOpenModalPriceDetail}
              onClose={closeModal}
            />
        </>
    );
}

export default BookingItemCard;
